import { useCallback, useEffect, useMemo, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster as SonnerToaster } from "sonner";
import "@/App.css";
import { apiClient } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import LoginPage from "@/pages/LoginPage";
import AuthCompletePage from "@/pages/AuthCompletePage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import ApplicantPage from "@/pages/ApplicantPage";
import ReviewPage from "@/pages/ReviewPage";
import QuestionsPage from "@/pages/QuestionsPage";

const LoadingScreen = () => (
  <div className="min-h-screen bg-background px-4 py-10 text-foreground">
    <div className="mx-auto flex min-h-[70vh] max-w-5xl items-center justify-center">
      <div className="w-full max-w-xl rounded-3xl border border-border bg-card p-8 shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
        <div className="mb-4 h-3 w-24 rounded-full bg-primary/40" />
        <div className="space-y-3">
          <div className="h-4 w-2/3 rounded-full bg-muted" />
          <div className="h-4 w-full rounded-full bg-muted" />
          <div className="h-4 w-4/5 rounded-full bg-muted" />
        </div>
      </div>
    </div>
  </div>
);

const HomeRoute = ({ authData, authLoading }) => {
  if (authLoading) return <LoadingScreen />;
  if (authData?.user) {
    return <Navigate to={authData.user.landing_route || "/apply"} replace />;
  }
  return <LoginPage />;
};

const ProtectedRoute = ({ authData, authLoading, children, requireReviewer = false, requireEditor = false }) => {
  const location = useLocation();

  if (authLoading) return <LoadingScreen />;
  if (!authData?.user) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }
  if (requireEditor && !authData.user.can_edit_questions) {
    return <Navigate to={authData.user.landing_route || "/review"} replace />;
  }
  if (requireReviewer && !authData.user.can_review) {
    return <Navigate to={authData.user.landing_route || "/apply"} replace />;
  }
  return children;
};

const ProtectedLayout = ({ authData, authLoading, onLogout, children }) => {
  if (authLoading) return <LoadingScreen />;
  return (
    <AppShell authData={authData} onLogout={onLogout}>
      {children}
    </AppShell>
  );
};

function App() {
  const [authData, setAuthData] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useEffect(() => {
    document.documentElement.classList.add("dark");
    document.body.classList.add("dark");
  }, []);

  const refreshAuth = useCallback(async () => {
    try {
      setAuthLoading(true);
      const response = await apiClient.get("/auth/me");
      setAuthData(response.data);
      return response.data;
    } catch (error) {
      setAuthData(null);
      return null;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshAuth();
  }, [refreshAuth]);

  const handleLogout = useCallback(async () => {
    await apiClient.post("/auth/logout");
    setAuthData(null);
    setAuthLoading(false);
  }, []);

  const routeProps = useMemo(
    () => ({ authData, authLoading, refreshAuth, onLogout: handleLogout }),
    [authData, authLoading, refreshAuth, handleLogout],
  );

  return (
    <div className="min-h-screen bg-background text-foreground">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomeRoute authData={authData} authLoading={authLoading} />} />
          <Route path="/auth/complete" element={<AuthCompletePage refreshAuth={refreshAuth} />} />
          <Route
            path="/admin-login"
            element={<AdminLoginPage refreshAuth={refreshAuth} authData={authData} authLoading={authLoading} />}
          />
          <Route
            path="/apply"
            element={(
              <ProtectedRoute authData={authData} authLoading={authLoading}>
                <ProtectedLayout {...routeProps}>
                  <ApplicantPage authData={authData} refreshAuth={refreshAuth} />
                </ProtectedLayout>
              </ProtectedRoute>
            )}
          />
          <Route
            path="/review"
            element={(
              <ProtectedRoute authData={authData} authLoading={authLoading} requireReviewer>
                <ProtectedLayout {...routeProps}>
                  <ReviewPage authData={authData} refreshAuth={refreshAuth} />
                </ProtectedLayout>
              </ProtectedRoute>
            )}
          />
          <Route
            path="/questions"
            element={(
              <ProtectedRoute authData={authData} authLoading={authLoading} requireEditor>
                <ProtectedLayout {...routeProps}>
                  <QuestionsPage authData={authData} refreshAuth={refreshAuth} />
                </ProtectedLayout>
              </ProtectedRoute>
            )}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <SonnerToaster theme="dark" richColors position="top-right" closeButton />
    </div>
  );
}

export default App;
